// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { CompressionSimpleTextValueVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectModel } from '@project/model/project.model';
import { AttachArticleUsageService } from '@project/service/attach-article-usage.service';
import { RemoveProjectArticleService } from '@project/service/remove-project-article.service';
import { ArticleModelDataHolder } from '@3d-scene/model3d/cache/model-data';
import { Log } from '@shared/debug/log';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { LabelEditorChanges } from '../aside-label-editor/aside-label-editor.component';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ISpanData } from '../span-data';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { PropertyOperation } from '@shared/undo/operation/property-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { IOperation } from '@shared/undo/operation/i-operation';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ModelLoadingService } from '@article/service/model-loading.service';
import { ProjectUtils } from '../../../project/util/project-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';


type ReplaceArticleResult = {
  success: true,
  operations: IOperation[]
} | {
  success: false
}

@Injectable({
  providedIn: 'root'
})
export class MarkingCardArticleService {

  constructor(
    private readonly projectModel: ProjectModel,
    private readonly projectArticleService: ProjectArticleService,
    private readonly attachArticleUsageService: AttachArticleUsageService,
    private readonly removeProjectArticleService: RemoveProjectArticleService,
    private readonly undoRedoUtils: UndoRedoUtils,
    private readonly applicationRefreshService: ApplicationRefreshService,
    private readonly modelLoadingService: ModelLoadingService,
  ) { }

  public async executeReplaceArticleUsage(newLabelArticle: ArticleVO, newMarkerCardArticle: ArticleVO, article: ArticleVO): Promise<ReplaceArticleResult> {
    const operations: IOperation[] = [];
    const oldArticleUsage = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    const undoDetachArticlesOperation: UndoDetachArticlesOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds([oldArticleUsage.id]);
    if (undoDetachArticlesOperation != null) {
      operations.push(undoDetachArticlesOperation);
    }

    // article has changed, for example by choosing a different color
    await this.removeProjectArticleService.removeArticles([oldArticleUsage.id], false);
    const newArticleUsage: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(newLabelArticle.identity, newLabelArticle.version);
    if (newMarkerCardArticle.metaElements != null) {
      for (const metaElement of newMarkerCardArticle.metaElements) {
        if (metaElement instanceof TextureAreaVO) {
          const textValue = new CompressionSimpleTextValueVO();
          textValue.text = '';
          ArticleUsageUtils.newTextureAreaUsage(newArticleUsage, metaElement.name, textValue);
        }
      }
    }
    await this.attachArticleUsageService.attachArticleUsageToProject(newArticleUsage, null, false);
    if (await this.applicationRefreshService.refreshProject()) {
      let articleModelDataHolder: ArticleModelDataHolder
      try {
        articleModelDataHolder = await this.modelLoadingService.loadModel(article, true);
      } catch (error) {
        Log.error();
        return {success: false};
      }

      article = articleModelDataHolder.article;
      const undoAttachArticlesOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds([newArticleUsage.id]);
      if (undoAttachArticlesOperation != null) {
        operations.push(undoAttachArticlesOperation);
      }
      operations.push(...this.saveLabeling(newArticleUsage, article));
      return {success: true, operations: operations};
    } else {
      // TODO
      return {success: false};
    }
  }

  public saveLabeling(articleUsage: ArticleUsageVO, article: ArticleVO, changes: LabelEditorChanges = null): IOperation[] {
    const tableProvider = Array.from(changes.tableProviderHandsontableOperationsMap.keys())[0];
    const operations: IOperation[] = [];

    for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleUsage)) {
      const corridor: number = MetaElementUsageUtils.getCorridorInformationOfTextureArea(textureAreaUsage, article);
      if (isNaN(corridor)) continue;

      const labelPos: number = corridor;
      if (labelPos <= 0 || labelPos > 100) continue;

      let row: number = Math.floor((labelPos - 1) / 10);
      let col: number = labelPos - row * 10 - 1;

      const tableSpanData: ISpanData = tableProvider.getSpanData(row * 2, col);
      let textValue = textureAreaUsage.textValue as CompressionSimpleTextValueVO;

      operations.push(this.updateTextValue(textValue, tableSpanData));
    }
    return operations;
  }

  private updateTextValue(textValue: CompressionSimpleTextValueVO, tableSpanData: ISpanData): PropertyOperation {
    // update textValue directly from tableSpanData (all required properties are on ISpanData)
    const oldValue: string = textValue.text;
    textValue.text = tableSpanData.data;
    textValue.vAlignment = TextureUtils.getIndexByVerticalAlign(tableSpanData.labelFormat.verticalAlign);
    textValue.rotation = tableSpanData.labelFormat.direction;
    textValue.compression = tableSpanData.compression;

    if (this.canUseAdvancedTextEditFunctions()) {
      textValue.autoCompression = tableSpanData.autoFormatByTextCheck;
      textValue.autoFontSize = tableSpanData.autoFormatByTextCheck;
    }

    // Create undo operation.
    return new PropertyOperation(
        textValue,
        'text',
        oldValue,
        textValue.text
    );
  }

  private canUseAdvancedTextEditFunctions(): boolean {
    return this.projectModel.user != null
      && this.projectModel.user.hasPermission("USE_ADVANCED_TEXT_EDIT_FUNCTIONS");
  }
}
